import { CASH, DcVppVehicleUsage, LEASE, OdometerMileageTypes } from '@makemydeal/dr-platform-types';
import {
    makeRequired,
    makeMaxLength,
    makeMinLength,
    makeRegex,
    RuleType,
    makeCustom,
    createDobValidator
} from '@makemydeal/dr-shared-ui-utils';
import { BUYER, CO_BUYER, LENDER, VEHICLE_ENTITY, VEHICLE_INSURANCE, GAP, TRADE_IN } from '@makemydeal/dr-shared-types';
import type { ValidationConfigMap } from '@makemydeal/dr-shared-ui-utils';
import { EVALUATION_TYPE_COLLECTION, EVALUATION_TYPE_NESTED } from '@makemydeal/dr-shared-ui-utils';

// Regex constant reused across county validations
const alphaSpacesRegex = /^[A-Za-z0-9\s]+$/;

const ELECTRIC = 'Electric';

// Matches maxLength on SaleVehicle.principalDriverName in the DXG spec.
export const PRINCIPAL_DRIVER_NAME_MAX_LENGTH = 100;

// Shared with the slideout input, which shows the same message while the name is being typed.
export const PRINCIPAL_DRIVER_NAME_TOO_LONG_MESSAGE = `Principal Driver Name can’t be longer than ${PRINCIPAL_DRIVER_NAME_MAX_LENGTH} characters.`;

// Prefix-matched on the contracting lender name; prefix not substring, so 'Rally ...' is excluded.
export const PRINCIPAL_DRIVER_REQUIRED_LENDER_NAMES = ['Ally', 'Stellantis'];

const isPrincipalDriverRequiredLender = (lenderName?: string | null): boolean => {
    const name = (lenderName ?? '').trim().toLowerCase();
    if (!name) return false;

    return PRINCIPAL_DRIVER_REQUIRED_LENDER_NAMES.some((lender) => name.startsWith(lender.toLowerCase()));
};

// Business use alone is not enough: only a lease (one-pay included) with one of the lenders above.
export const isPrincipalDriverNameRequired = ({
    vehicleUse,
    offerType,
    lenderName
}: {
    vehicleUse?: string;
    offerType?: string | null;
    lenderName?: string | null;
}): boolean =>
    vehicleUse === DcVppVehicleUsage.BUSINESS && offerType?.toLowerCase() === LEASE && isPrincipalDriverRequiredLender(lenderName);

export const getLienholderConfig = (preferInlineMessage: boolean = false): ValidationConfigMap => {
    return {
        // Required only when trade-in payoff amount exceeds $0
        name: {
            fieldName: 'name',
            objectType: TRADE_IN,
            rules: [
                {
                    type: RuleType.REQUIRED,
                    message: !preferInlineMessage
                        ? 'Trade In lienholder name is required if the Trade In payoff amount exceeds $0.'
                        : 'Trade-in lienholder name is required if payoff amount exceeds $0.',
                    when: isTradeInPayoffPositive
                }
            ]
        },
        streetAddress: {
            fieldName: 'streetAddress',
            objectType: TRADE_IN,
            rules: [
                makeMaxLength(
                    60,
                    !preferInlineMessage
                        ? 'Lienholder Bank Address cannot be longer than 60 characters.'
                        : 'Bank address can’t be longer than 60 characters.'
                )
            ]
        },
        city: {
            fieldName: 'city',
            objectType: TRADE_IN,
            rules: [
                makeMaxLength(
                    30,
                    !preferInlineMessage
                        ? 'Lienholder City cannot be longer than 30 characters.'
                        : 'City can’t be longer than 30 characters.'
                )
            ]
        },
        state: {
            fieldName: 'state',
            objectType: TRADE_IN,
            rules: [
                makeMaxLength(
                    2,
                    !preferInlineMessage
                        ? 'Lienholder State cannot be longer than 2 characters.'
                        : 'State can’t be longer than 2 characters.'
                )
            ]
        },
        zip: {
            fieldName: 'zip',
            objectType: TRADE_IN,
            rules: [
                {
                    type: RuleType.MIN_LENGTH,
                    value: 5,
                    message: !preferInlineMessage
                        ? 'Lienholder Zip cannot be less than 5 characters.'
                        : 'Zip code must be at least 5 characters.',
                    ...(preferInlineMessage ? { when: isLienholderFieldHasContent } : {})
                },
                {
                    type: RuleType.MAX_LENGTH,
                    value: 9,
                    message: !preferInlineMessage
                        ? 'Lienholder Zip cannot be longer than 9 characters.'
                        : 'Zip code can’t be longer than 9 characters.',
                    ...(preferInlineMessage ? { when: isLienholderFieldHasContent } : {})
                }
            ]
        }
    };
};

export const tradeInConfig: ValidationConfigMap = {
    year: {
        rules: [makeRequired('Year is required for trade-in.')]
    },
    make: {
        rules: [makeRequired('Make is required for trade-in.')]
    },
    model: {
        rules: [makeRequired('Model is required for trade-in.')]
    },
    trim: {
        rules: [makeRequired('Trim is required for trade-in.')]
    }
};

export const getTradeInsConfig = (preferInlineMessage: boolean = false): ValidationConfigMap => {
    return {
        year: {
            rules: [makeRequired('Year is required.')]
        },
        make: {
            rules: [makeRequired('Make is required.')]
        },
        model: {
            rules: [makeRequired('Model is required.')]
        },
        trim: {
            rules: [makeRequired('Trim is required.')]
        },
        lienholder: {
            evaluationType: EVALUATION_TYPE_NESTED,
            config: getLienholderConfig(preferInlineMessage)
        }
    };
};

export const getAccessoryNameConfig = (preferInlineMessage: boolean = false): ValidationConfigMap => {
    return {
        name: {
            objectType: 'accessories',
            fieldName: 'name',
            rules: [
                makeMaxLength(
                    50,
                    !preferInlineMessage
                        ? 'Name cannot be longer than 50 characters.'
                        : 'Accessory name cannot be longer than 50 characters.'
                )
            ]
        }
    };
};

export const getContractFieldValidationConfig = (preferInlineMessage: boolean = false): ValidationConfigMap => {
    return {
        'buyer.firstName': {
            objectType: BUYER,
            fieldName: 'firstName',
            rules: [
                makeRequired(!preferInlineMessage ? 'Applicant Legal First Name is required.' : 'First Name is required.'),
                makeMaxLength(
                    15,
                    !preferInlineMessage
                        ? 'Applicant Legal First Name cannot be longer than 15 characters.'
                        : 'First name can’t be longer than 15 characters.'
                )
            ]
        },
        'buyer.middleName': {
            objectType: BUYER,
            fieldName: 'middleName',
            rules: [
                makeMaxLength(
                    15,
                    !preferInlineMessage
                        ? 'Applicant Legal Middle Name cannot be longer than 15 characters.'
                        : 'Middle name can’t be longer than 15 characters.'
                )
            ]
        },
        'buyer.lastName': {
            objectType: BUYER,
            fieldName: 'lastName',
            rules: [
                makeRequired(!preferInlineMessage ? 'Applicant Legal Last Name is required.' : 'Last Name is required.'),
                makeMaxLength(
                    25,
                    !preferInlineMessage
                        ? 'Applicant Legal Last Name cannot be longer than 25 characters.'
                        : 'Last name can’t be longer than 25 characters.'
                )
            ]
        },
        'buyer.streetAddress': {
            objectType: BUYER,
            fieldName: 'streetAddress',
            rules: [
                makeRequired(!preferInlineMessage ? 'Applicant Street Address is required.' : 'Street Address is required.'),
                makeMaxLength(
                    60,
                    !preferInlineMessage
                        ? 'Applicant Street Address cannot be longer than 60 characters.'
                        : 'Street address can’t be longer than 60 characters.'
                )
            ]
        },
        'lenderProgram.name': {
            objectType: LENDER,
            fieldName: 'name',
            rules: [
                {
                    type: RuleType.REQUIRED,
                    message: 'Lender selection is required for Finance and Lease deals.',
                    excludeOfferTypes: [CASH]
                }
            ]
        },
        'buyer.streetAddress2': {
            objectType: BUYER,
            fieldName: 'streetAddress2',
            rules: [
                makeMaxLength(
                    60,
                    !preferInlineMessage
                        ? 'Applicant Street Address 2 cannot be longer than 60 characters.'
                        : 'Street address 2 can’t be longer than 60 characters.'
                )
            ]
        },
        'buyer.city': {
            objectType: BUYER,
            fieldName: 'city',
            rules: [
                makeRequired(!preferInlineMessage ? 'Applicant City is required.' : 'City is required.'),
                makeMaxLength(
                    30,
                    !preferInlineMessage
                        ? 'Applicant City cannot be longer than 30 characters.'
                        : 'City can’t be longer than 30 characters.'
                )
            ]
        },
        'buyer.state': {
            objectType: BUYER,
            fieldName: 'state',
            rules: [
                makeMaxLength(
                    2,
                    !preferInlineMessage
                        ? 'Applicant State cannot be longer than 2 characters.'
                        : 'State can’t be longer than 2 characters.'
                )
            ]
        },
        'buyer.county': {
            objectType: BUYER,
            fieldName: 'county',
            rules: [
                makeMaxLength(
                    20,
                    !preferInlineMessage
                        ? 'Applicant County cannot be longer than 20 characters.'
                        : 'County can’t be longer than 20 characters.'
                ),
                makeRegex(
                    alphaSpacesRegex,
                    !preferInlineMessage
                        ? 'Applicant County cannot have special characters.'
                        : 'County can’t include special characters.'
                )
            ]
        },
        'buyer.zip': {
            objectType: BUYER,
            fieldName: 'zip',
            rules: [
                makeMinLength(
                    5,
                    !preferInlineMessage
                        ? 'Applicant Postal Code cannot be less than 5 characters.'
                        : 'Postal code must be at least 5 characters.'
                ),
                makeMaxLength(
                    9,
                    !preferInlineMessage
                        ? 'Applicant Postal Code cannot be longer than 9 characters.'
                        : 'Postal code can’t be longer than 9 characters.'
                )
            ]
        },
        'buyer.email': {
            objectType: BUYER,
            fieldName: 'email',
            rules: [
                makeRequired(
                    !preferInlineMessage ? 'Applicant Primary Email Address is required.' : 'Primary Email Address is required.'
                )
            ]
        },
        'coBuyer.firstName': {
            objectType: CO_BUYER,
            fieldName: 'firstName',
            rules: [
                makeRequired(!preferInlineMessage ? 'Co-Applicant Legal First Name is required.' : 'First Name is required.'),
                makeMaxLength(
                    15,
                    !preferInlineMessage
                        ? 'Co-Applicant Legal First Name cannot be longer than 15 characters.'
                        : 'First name can’t be longer than 15 characters.'
                )
            ]
        },
        'coBuyer.middleName': {
            objectType: CO_BUYER,
            fieldName: 'middleName',
            rules: [
                makeMaxLength(
                    15,
                    !preferInlineMessage
                        ? 'Co-Applicant Legal Middle Name cannot be longer than 15 characters.'
                        : 'Middle name can’t be longer than 15 characters.'
                )
            ]
        },
        'coBuyer.lastName': {
            objectType: CO_BUYER,
            fieldName: 'lastName',
            rules: [
                makeRequired(!preferInlineMessage ? 'Co-Applicant Legal Last Name is required.' : 'Last Name is required.'),
                makeMaxLength(
                    25,
                    !preferInlineMessage
                        ? 'Co-Applicant Legal Last Name cannot be longer than 25 characters.'
                        : 'Last name can’t be longer than 25 characters.'
                )
            ]
        },
        'coBuyer.streetAddress': {
            objectType: CO_BUYER,
            fieldName: 'streetAddress',
            rules: [
                makeRequired(!preferInlineMessage ? 'Co-Applicant Street Address is required.' : 'Street Address is required.'),
                makeMaxLength(
                    60,
                    !preferInlineMessage
                        ? 'Co-Applicant Street Address cannot be longer than 60 characters.'
                        : 'Street Address can’t be longer than 60 characters.'
                )
            ]
        },
        'coBuyer.streetAddress2': {
            objectType: CO_BUYER,
            fieldName: 'streetAddress2',
            rules: [
                makeMaxLength(
                    60,
                    !preferInlineMessage
                        ? 'Co-Applicant Street Address 2 cannot be longer than 60 characters.'
                        : 'Street Address 2 can’t be longer than 60 characters.'
                )
            ]
        },
        'coBuyer.city': {
            objectType: CO_BUYER,
            fieldName: 'city',
            rules: [
                makeRequired(!preferInlineMessage ? 'Co-Applicant City is required.' : 'City is required.'),
                makeMaxLength(
                    30,
                    !preferInlineMessage
                        ? 'Co-Applicant City cannot be longer than 30 characters.'
                        : 'City can’t be longer than 30 characters.'
                )
            ]
        },
        'coBuyer.state': {
            objectType: CO_BUYER,
            fieldName: 'state',
            rules: [
                makeMaxLength(
                    2,
                    !preferInlineMessage
                        ? 'Co-Applicant State cannot be longer than 2 characters.'
                        : 'State can’t be longer than 2 characters.'
                )
            ]
        },
        'coBuyer.county': {
            objectType: CO_BUYER,
            fieldName: 'county',
            rules: [
                makeMaxLength(
                    20,
                    !preferInlineMessage
                        ? 'Co-Applicant County cannot be longer than 20 characters.'
                        : 'County can’t be longer than 20 characters.'
                ),
                makeRegex(
                    alphaSpacesRegex,
                    !preferInlineMessage
                        ? 'Co-Applicant County cannot have special characters.'
                        : 'County can’t have special characters.'
                )
            ]
        },
        'coBuyer.zip': {
            objectType: CO_BUYER,
            fieldName: 'zip',
            rules: [
                makeMinLength(
                    5,
                    !preferInlineMessage
                        ? 'Co-Applicant Postal Code cannot be less than 5 characters.'
                        : 'Postal code must be at least 5 characters.'
                ),
                makeMaxLength(
                    9,
                    !preferInlineMessage
                        ? 'Co-Applicant Postal Code cannot be longer than 9 characters.'
                        : 'Postal Code can’t be longer than 9 characters.'
                )
            ]
        },
        'coBuyer.email': {
            objectType: CO_BUYER,
            fieldName: 'email',
            rules: [
                {
                    type: RuleType.REQUIRED,
                    message: !preferInlineMessage
                        ? 'Co-Applicant Primary Email Address is required.'
                        : 'Primary Email Address is required.',
                    when: (input: any) => Boolean(input?.coBuyer)
                }
            ]
        },
        // Required only when trade-in payoff amount exceeds $0
        'lienholder.name': {
            rules: [
                {
                    type: RuleType.REQUIRED,
                    message: 'Trade In lienholder name is required if the Trade In payoff amount exceeds $0.',
                    when: isTradeInPayoffPositive
                }
            ]
        },
        'lienholder.streetAddress': {
            rules: [makeMaxLength(60, 'Lienholder Bank Address cannot be longer than 60 characters.')]
        },
        'lienholder.city': {
            rules: [makeMaxLength(30, 'Lienholder City cannot be longer than 30 characters.')]
        },
        'lienholder.state': {
            rules: [makeMaxLength(2, 'Lienholder State cannot be longer than 2 characters.')]
        },
        'lienholder.zip': {
            rules: [
                makeMinLength(5, 'Lienholder Zip cannot be less than 5 characters.'),
                makeMaxLength(9, 'Lienholder Zip cannot be longer than 9 characters.')
            ]
        },
        accessories: {
            evaluationType: EVALUATION_TYPE_COLLECTION,
            config: getAccessoryNameConfig(preferInlineMessage),
            errorPrefix: 'Accessory'
        },
        'gap.streetAddress': {
            objectType: GAP,
            fieldName: 'streetAddress',
            rules: [
                makeRequired(!preferInlineMessage ? 'GAP After Market Address is required.' : 'Address is required.'),
                makeMaxLength(
                    60,
                    !preferInlineMessage
                        ? 'GAP After Market Address cannot be longer than 60 characters.'
                        : 'Address can’t be longer than 60 characters.'
                )
            ]
        },
        'gap.city': {
            objectType: GAP,
            fieldName: 'city',
            rules: [
                makeRequired(!preferInlineMessage ? 'GAP After Market City is required.' : 'City is required.'),
                makeMaxLength(
                    30,
                    !preferInlineMessage
                        ? 'GAP After Market City cannot be longer than 30 characters.'
                        : 'City can’t be longer than 30 characters.'
                )
            ]
        },
        'gap.state': {
            objectType: GAP,
            fieldName: 'state',
            rules: [
                makeMaxLength(
                    2,
                    !preferInlineMessage
                        ? 'GAP After Market State cannot be longer than 2 characters.'
                        : 'State can’t be longer than 2 characters.'
                )
            ]
        },
        'gap.zip': {
            objectType: GAP,
            fieldName: 'zip',
            rules: [
                makeMinLength(
                    5,
                    !preferInlineMessage
                        ? 'GAP After Market Postal Code cannot be less than 5 characters.'
                        : 'Postal Code must be at least 5 characters.'
                ),
                makeMaxLength(
                    9,
                    !preferInlineMessage
                        ? 'GAP After Market Postal Code cannot be longer than 9 characters.'
                        : 'Postal Code cannot be longer than 9 characters.'
                )
            ]
        },
        'vehicle.bookoutOrInvoice': {
            objectType: VEHICLE_ENTITY,
            fieldName: 'bookoutOrInvoice',
            rules: [
                {
                    ...makeCustom('Invoice is required.', (input: any) => Boolean(input.invoiceAmount)),
                    when: (input: any) => input.offerType === CASH
                },
                {
                    ...makeCustom('Enter the invoice amount or use bookout value + source', (input: any) => {
                        const isBookoutFieldsValid = Boolean(input.bookoutSource && input.bookoutValue);
                        return Boolean(isBookoutFieldsValid || input.invoiceAmount);
                    }),
                    excludeOfferTypes: [CASH]
                }
            ]
        },
        'vehicle.cylinders': {
            objectType: VEHICLE_ENTITY,
            fieldName: 'cylinders',
            rules: [
                {
                    type: RuleType.REQUIRED,
                    applicableStates: ['NV', 'AZ'],
                    excludeFuelTypes: [ELECTRIC],
                    message: 'Number of Cylinders is required.'
                }
            ]
        },
        'vehicle.vehicleUse': {
            objectType: VEHICLE_ENTITY,
            fieldName: 'vehicleUse',
            rules: [
                {
                    ...makeRequired(!preferInlineMessage ? 'Primary Use is required.' : 'Required to generate contract')
                }
            ]
        },
        'vehicle.principalDriverName': {
            objectType: VEHICLE_ENTITY,
            fieldName: 'principalDriverName',
            rules: [
                {
                    ...makeCustom(
                        !preferInlineMessage ? 'Principal Driver Name is required.' : 'Required to generate contract',
                        (input: any) => {
                            const { principalDriverName, ...deal } = input ?? {};
                            if (!isPrincipalDriverNameRequired(deal)) return true;
                            return Boolean(String(principalDriverName ?? '').trim());
                        }
                    )
                },
                {
                    // Not makeMaxLength: this field's inline value is the object the required rule needs,
                    // and the built-in MAX_LENGTH evaluator passes anything that is not a string.
                    ...makeCustom(
                        !preferInlineMessage
                            ? `Principal Driver Name cannot be longer than ${PRINCIPAL_DRIVER_NAME_MAX_LENGTH} characters.`
                            : PRINCIPAL_DRIVER_NAME_TOO_LONG_MESSAGE,
                        (input: any) => {
                            const { principalDriverName } = input ?? {};
                            return String(principalDriverName ?? '').length <= PRINCIPAL_DRIVER_NAME_MAX_LENGTH;
                        }
                    )
                }
            ]
        },
        'vehicle.odometerMileageType': {
            objectType: VEHICLE_ENTITY,
            fieldName: 'odometerMileageType',
            rules: [
                {
                    type: RuleType.EQUALS,
                    value: OdometerMileageTypes.Actual,
                    applicableStates: ['OH'],
                    message: 'Actual Odometer Mileage is required.'
                }
            ]
        },
        tradeIn: {
            evaluationType: EVALUATION_TYPE_NESTED,
            config: tradeInConfig
        },
        tradeIns: {
            evaluationType: EVALUATION_TYPE_COLLECTION,
            config: getTradeInsConfig(preferInlineMessage),
            errorPrefix: 'Trade'
        },
        tradeInLienholder: {
            evaluationType: EVALUATION_TYPE_NESTED,
            config: getLienholderConfig(preferInlineMessage)
        },
        'vehicleInsurance.policyNumber': {
            fieldName: 'policyNumber',
            objectType: VEHICLE_INSURANCE,
            rules: [makeMaxLength(20, 'Insurance policy number must be less than 20 characters.')]
        },
        'vehicleInsurance.liabilityCoverage': {
            fieldName: 'liabilityCoverage',
            objectType: VEHICLE_INSURANCE,
            rules: [
                {
                    type: RuleType.EQUALS,
                    value: 'true',
                    applicableStates: ['CO'],
                    message: 'Buyer liability insurance statement is required.'
                }
            ]
        },
        applicantEmails: {
            objectType: CO_BUYER,
            fieldName: 'email',
            rules: [{ type: RuleType.DISTINCT_VALUES, message: 'Buyer and Co-buyer email addresses cannot be identical.' }]
        },
        'buyer.dateOfBirth': {
            objectType: BUYER,
            fieldName: 'dateOfBirth',
            rules: [
                createDobValidator(
                    !preferInlineMessage
                        ? 'Applicant Date of Birth must be prior to current date and within the last 100 years.'
                        : 'Date of Birth must be prior to current date and within the last 100 years.'
                )
            ]
        },
        'coBuyer.dateOfBirth': {
            objectType: CO_BUYER,
            fieldName: 'dateOfBirth',
            rules: [
                createDobValidator(
                    !preferInlineMessage
                        ? 'Co-Applicant Date of Birth must be prior to current date and within the last 100 years.'
                        : 'Date of Birth must be prior to current date and within the last 100 years.'
                )
            ]
        }
    };
};

export function isTradeInPayoffPositive(input: any): boolean {
    // For collection items, amountOwed is at root level
    // For single trade-in, it's nested under tradeIn
    const amountOwed = input?.amountOwed ?? input?.tradeIn?.amountOwed ?? 0;
    return amountOwed > 0;
}

export function isLienholderFieldHasContent(input: any): boolean {
    // Check if any lienholder field has content
    const lienholder = input?.lienholder || input?.tradeIn?.lienholder;
    if (!lienholder) return false;

    const accountNumber = lienholder.accountNumber || '';
    const streetAddress = lienholder.streetAddress || '';
    const city = lienholder.city || '';
    const state = lienholder.state || '';
    const zip = lienholder.zip || '';

    // Return true if any field has content (trimmed non-empty string)
    return (
        accountNumber.trim().length > 0 ||
        streetAddress.trim().length > 0 ||
        city.trim().length > 0 ||
        state.trim().length > 0 ||
        zip.trim().length > 0
    );
}
